import { z } from "zod";
import { classAssignmentSchema } from "@common/schemas/class.schema";
import { ClassFilterType } from "@common/types/class";
import {
    CatalogItemArchivedFilter,
    CatalogItemSort,
    CatalogItemSource,
    CatalogItemType,
} from "@common/types/catalogItem";
import { InvoiceSource } from "@common/types/invoice";
import {
    catalogItemPriceOperationsSchema,
    createCatalogItemPriceOperationsSchema,
} from "./catalogItemPrice.schema";
import { currencySchema, unitAmountSchema } from "./catalogItemMoney.schema";

export { unitAmountSchema, currencySchema } from "./catalogItemMoney.schema";

// Every item requires a non-empty name (also enforced at the DB level by the
// chk_catalog_item_name_not_empty CHECK constraint). Description stays optional.
const catalogItemNameSchema = z.string().trim().min(1).max(255);

// Unrefined base shape, exported separately so callers that need to
// `.extend()` it (e.g. agentTools' MCP-stricter create schema) can do so —
// `.refine()` returns a ZodEffects, which can't be extended. Callers
// extending this base are responsible for re-applying
// assertCreatePricesUnitAmountExclusive if they keep both fields
// optional/present.
export const createCatalogItemObjectSchema = z.object({
    name: catalogItemNameSchema,
    description: z.string().max(1000).nullable().optional(),
    sku: z.string().max(255).nullable().optional(),
    type: z.nativeEnum(CatalogItemType).default(CatalogItemType.SERVICE),
    unitAmount: unitAmountSchema.nullable().optional(),
    currency: currencySchema.default("usd"),
    // Description of the initial price created from `unitAmount`. Distinct
    // from the item's own `description`. Only stored when `unitAmount` is
    // set (no price row exists otherwise).
    priceDescription: z.string().max(1000).nullable().optional(),
    // Multiple starting prices in one request (e.g. several currencies) —
    // applied via the same batched price-operation logic updateCatalogItem
    // uses (CatalogItemMutationInternalService.applyPriceOperations), inside
    // the item's create transaction. Mutually exclusive with unitAmount: see
    // assertCreatePricesUnitAmountExclusive.
    prices: createCatalogItemPriceOperationsSchema.optional(),
    // Default accrual/receivables accounts propagated onto invoice line coding
    // when this item is selected. Validated (workspace + entity + type) at the
    // service layer.
    revenueAccountId: z.string().uuid().nullable().optional(),
    receivablesAccountId: z.string().uuid().nullable().optional(),
    // Required: every catalog item belongs to exactly one entity. Entity IDs are
    // auto-increment integers starting at 1, so 0/negatives are never valid.
    entityId: z.coerce.number().int().min(1),
    // Default classes copied onto an invoice line's classAssignments when the
    // item is selected. At most one class per root class dimension (enforced
    // server-side, same rule as invoice line item classes). Omitted/empty = no
    // default classes.
    classIds: z.array(z.string().uuid()).optional(),
});

// unitAmount is the single-price shorthand; `prices` is the explicit
// multi-price batch. Combining them would leave two different starting
// prices from two unrelated code paths — keep the two mutation modes
// mutually exclusive so each request has one unambiguous meaning (mirrors
// the same guard on updateCatalogItemSchema). Exported so callers that
// `.extend()` createCatalogItemObjectSchema can re-apply the same guard.
export function assertCreatePricesUnitAmountExclusive<
    T extends { prices?: unknown[]; unitAmount?: number | null },
>(value: T): boolean {
    // An empty `prices: []` contributes no starting price, so it doesn't
    // conflict with `unitAmount` — treat "no prices" length-aware, matching the
    // MCP schema's own `(prices?.length ?? 0) > 0` "at least one" convention.
    return (value.prices?.length ?? 0) === 0 || value.unitAmount == null;
}

export const createCatalogItemSchema = createCatalogItemObjectSchema.refine(
    assertCreatePricesUnitAmountExclusive,
    {
        message:
            "prices cannot be combined with unitAmount; use one price creation mode per request",
        path: ["prices"],
    },
);

// Unrefined base shape, exported separately so callers that need to
// `.omit()` a field (e.g. agentTools' MCP schema, which drops `currency`,
// `type`, and `sku`) can rebuild the same partial+refine pipeline.
// `.refine()` returns a ZodEffects, which can't be omitted. Callers building
// on this base are responsible for re-applying assertUpdateHasAtLeastOneField
// and assertUpdatePricesExclusiveOfUnitAmount if they want the same guards.
export const updateCatalogItemObjectSchema = z.object({
    name: catalogItemNameSchema,
    description: z.string().max(1000).nullable(),
    sku: z.string().max(255).nullable(),
    type: z.nativeEnum(CatalogItemType),
    // A change to unitAmount/currency rotates the active price server-side.
    unitAmount: unitAmountSchema.nullable(),
    currency: currencySchema,
    active: z.boolean(),
    revenueAccountId: z.string().uuid().nullable(),
    receivablesAccountId: z.string().uuid().nullable(),
    entityId: z.coerce.number().int().min(1),
    classIds: z.array(z.string().uuid()),
    // Batch price create/archive/enable/delete/set-default/description
    // operations, applied inside the same transaction as the item's own
    // field changes (see
    // CatalogItemMutationInternalService.applyPriceOperations). This is
    // the ONLY way a price is ever mutated — there is no single-price
    // create/update/delete endpoint, so nothing about a price can persist
    // before the item's own Save commits this request.
    prices: catalogItemPriceOperationsSchema,
});

export function assertUpdateHasAtLeastOneField<T extends object>(
    value: T,
): boolean {
    return Object.keys(value).length > 0;
}

// unitAmount/currency rotate the item's single "current" price (deactivate
// every active price, insert a replacement), while `prices` applies an
// explicit batch against the current price set. Combining them would run
// the batch against a state the rotation just changed out from under it —
// creating an unintended extra price or a spurious conflict. Keep the two
// mutation modes mutually exclusive so each request has one unambiguous
// meaning. Exported so callers that build on updateCatalogItemObjectSchema
// can re-apply the same guard. `currency` stays optional so a caller whose
// object never has that key (agentTools' MCP update schema drops it and
// server-sets usd) still satisfies the check.
export function assertUpdatePricesExclusiveOfUnitAmount<
    T extends { prices?: unknown; unitAmount?: unknown; currency?: unknown },
>(value: T): boolean {
    return (
        value.prices === undefined ||
        (value.unitAmount === undefined && value.currency === undefined)
    );
}

export const updateCatalogItemSchema = updateCatalogItemObjectSchema
    .partial()
    .refine(assertUpdateHasAtLeastOneField, {
        message: "At least one item field must be provided",
    })
    .refine(assertUpdatePricesExclusiveOfUnitAmount, {
        message:
            "prices cannot be combined with unitAmount/currency; use one price mutation mode per request",
        path: ["prices"],
    });

export const listCatalogItemsQuerySchema = z
    .object({
        search: z.string().max(255).optional(),
        // Empty/omitted = no id filter. A non-empty subset filters to items
        // whose id is in the selection (used to resolve specific items by id,
        // e.g. `get` and invoice compose). Bounded to keep the IN (...) list
        // from growing unbounded; matches the MAX_LIST_CATALOG_ITEMS cap.
        ids: z.array(z.string().uuid()).max(1000).optional(),
        // Empty/omitted = all entities (no scoping). A non-empty subset filters
        // to items whose entityId is in the selection.
        entityIds: z.array(z.coerce.number().int()).optional(),
        archivedFilter: z.nativeEnum(CatalogItemArchivedFilter).optional(),
        sort: z.nativeEnum(CatalogItemSort).optional(),
        // Empty/omitted = all accounts. A non-empty subset filters to items that
        // appear on an invoice of one of the selected sources (Kick additionally
        // includes uncategorized catalog items with no invoice association).
        sources: z.array(z.nativeEnum(InvoiceSource)).optional(),
        // Empty/omitted = no class filter (except classFilterType=no_classes,
        // which needs no ids). Selecting a class includes its descendants.
        classIds: z.array(z.string().uuid()).max(1000).optional(),
        // How classIds is matched against an item's default classes. Defaults to
        // any_selected. has_classes is a transaction-filter option the catalog
        // overlay does not offer, so it is not accepted here.
        classFilterType: z
            .enum([
                ClassFilterType.ANY_SELECTED,
                ClassFilterType.ALL_SELECTED,
                ClassFilterType.EXCLUDE_SELECTED,
                ClassFilterType.NO_CLASSES,
            ])
            .optional(),
        // Empty/omitted = no account filter. A non-empty subset filters to items
        // whose default income (revenue) / receivables account is one of the
        // ids. Accounts are entity-scoped, so a multi-entity selection carries
        // the matching account id of each entity.
        incomeAccountIds: z.array(z.string().uuid()).max(1000).optional(),
        receivablesAccountIds: z.array(z.string().uuid()).max(1000).optional(),
        // Both omitted = unpaginated (full matching set, up to the existing
        // MAX_LIST_CATALOG_ITEMS cap) so existing callers (e.g. the invoice line
        // item catalog item combobox) are unaffected. Only the Items list page
        // passes both to page through results.
        page: z.coerce.number().int().min(1).optional(),
        limit: z.coerce.number().int().min(1).max(200).optional(),
    })
    // page/limit are strictly both-or-nothing: passing only one is a caller
    // error, not a silent fall-through to the unpaginated set.
    .refine(
        (value) => (value.page === undefined) === (value.limit === undefined),
        { message: "page and limit must be provided together" },
    );

export const bulkSetCatalogItemsActiveSchema = z.object({
    catalogItemIds: z
        .array(z.string().uuid())
        .min(1)
        .max(200)
        .refine((ids) => new Set(ids).size === ids.length, {
            message: "catalogItemIds must not contain duplicates",
        }),
    active: z.boolean(),
});

export const bulkSetCatalogItemsActiveResultSchema = z.object({
    updatedIds: z.array(z.string().uuid()),
    conflictIds: z.array(z.string().uuid()),
    ignoredIds: z.array(z.string().uuid()),
});

export type CreateCatalogItemBody = z.infer<typeof createCatalogItemSchema>;
export type UpdateCatalogItemBody = z.infer<typeof updateCatalogItemSchema>;
export type ListCatalogItemsQuery = z.infer<typeof listCatalogItemsQuerySchema>;
export type BulkSetCatalogItemsActiveBody = z.infer<
    typeof bulkSetCatalogItemsActiveSchema
>;
export type BulkSetCatalogItemsActiveResult = z.infer<
    typeof bulkSetCatalogItemsActiveResultSchema
>;

export const catalogItemPriceRowSchema = z.object({
    id: z.string().uuid(),
    catalogItemId: z.string().uuid(),
    unitAmount: z.number(),
    currency: z.string(),
    active: z.boolean(),
    isDefault: z.boolean(),
    stripePriceId: z.string().nullable(),
    description: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type CatalogItemPriceRow = z.infer<typeof catalogItemPriceRowSchema>;
export type CatalogItemPrice = CatalogItemPriceRow;

export const catalogItemRowSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    workspaceId: z.string().uuid(),
    entityId: z.number().int(),
    name: z.string(),
    description: z.string().nullable(),
    sku: z.string().nullable(),
    type: z.nativeEnum(CatalogItemType),
    active: z.boolean(),
    revenueAccountId: z.string().uuid().nullable(),
    receivablesAccountId: z.string().uuid().nullable(),
    stripeProductId: z.string().nullable(),
    source: z.nativeEnum(CatalogItemSource).nullable(),
    sourceId: z.string().nullable(),
});
export type CatalogItemRow = z.infer<typeof catalogItemRowSchema>;

export const catalogItemClassRowSchema = z.object({
    classId: z.string().uuid(),
    catalogItemId: z.string().uuid(),
    rootClassId: z.string().uuid(),
});
export type CatalogItemClassRow = z.infer<typeof catalogItemClassRowSchema>;

export const catalogItemLoadedSchema = catalogItemRowSchema.extend({
    prices: z.array(catalogItemPriceRowSchema).optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    invoiceCount: z.number().int().optional(),
    templateCount: z.number().int().optional(),
});
export type CatalogItem = z.infer<typeof catalogItemLoadedSchema>;
